/* FX recipes + per-channel FX-chain factory extracted from audioEngine.js.
 *
 * NOTHING here references the Channel or AudioEngine classes — these are
 * pure WebAudio utilities. Splitting them out keeps the main file readable
 * and lets the offline mastering pipeline reuse the exact same recipes for
 * deterministic export rendering.
 *
 * The FX_PARAM_DEFAULTS + FX_PARAM_SCHEMA exports are consumed by the React
 * Effects Rack UI to drive knob ranges, units, and labels. EFFECT_KEYS is
 * the canonical iteration order (also drives the toggle row order). */
import { makeReverbIR } from "@/lib/audioEngineUtils";

export const EFFECT_KEYS = ["autotune", "eq", "compressor", "reverb", "delay", "limiter"];

/** Default param values for each effect — applied when the effect is toggled
 *  ON and live-mutated via the Effects Rack knobs. Each Channel keeps its
 *  own clone in `channel.fxParams` so per-channel tweaks don't leak across
 *  the mixer.
 *
 *  Ranges match common DAW conventions:
 *    eq.*              dB gain, -12..+12
 *    compressor.threshold  dB, -60..0  · compressor.ratio  1..20
 *    limiter.threshold     dB, -24..0  · limiter.ratio     1..20
 *    reverb.send       0..1 (wet send; dry always 1)
 *    delay.time        seconds 0.001..2  · delay.feedback 0..0.95
 *    autotune.depth    cents 0..30  · autotune.rate Hz 0.5..12 */
export const FX_PARAM_DEFAULTS = {
  eq:         { low: 4, mid: -2.5, high: 3 },
  compressor: { threshold: -18, ratio: 4 },
  limiter:    { threshold: -3, ratio: 20 },
  // iter-79: cleaner-by-default. 0.15 is audible but doesn't wash the mix.
  reverb:     { send: 0.15 },
  delay:      { time: 0.38, feedback: 0.4 },
  autotune:   { depth: 14, rate: 4.5 },
};

/** Param schema for the UI — drives knob ranges, units, and labels. */
export const FX_PARAM_SCHEMA = {
  eq: {
    low:  { min: -12, max: 12, step: 0.1, unit: "dB", label: "Low" },
    mid:  { min: -12, max: 12, step: 0.1, unit: "dB", label: "Mid" },
    high: { min: -12, max: 12, step: 0.1, unit: "dB", label: "High" },
  },
  compressor: {
    threshold: { min: -60, max: 0, step: 0.5, unit: "dB", label: "Threshold" },
    ratio:     { min: 1, max: 20, step: 0.1, unit: ":1", label: "Ratio" },
  },
  limiter: {
    threshold: { min: -24, max: 0, step: 0.1, unit: "dB", label: "Ceiling" },
    ratio:     { min: 1, max: 20, step: 0.5, unit: ":1", label: "Ratio" },
  },
  reverb: {
    send: { min: 0, max: 1, step: 0.01, unit: "", label: "Wet" },
  },
  delay: {
    time:     { min: 0.001, max: 2, step: 0.01, unit: "s", label: "Time" },
    feedback: { min: 0, max: 0.95, step: 0.01, unit: "", label: "Feedback" },
  },
  autotune: {
    depth: { min: 0, max: 30, step: 0.1, unit: "¢", label: "Depth" },
    rate:  { min: 0.5, max: 12, step: 0.1, unit: "Hz", label: "Rate" },
  },
};

/** Effect param recipes — receives ({ nodes, t, on, params }).
 *  When `on === false`, push identity values so the dry signal passes through
 *  (no graph rewiring needed). Times for setTargetAtTime are deliberately
 *  short (≤ 0.05 s) so knob tweaks feel instant without zipper artifacts. */
const EFFECT_RECIPES = {
  eq: ({ nodes, t, on, params }) => {
    const T = 0.02;
    nodes.eqLow.gain.setTargetAtTime(on ? params.low : 0, t, T);
    nodes.eqMid.gain.setTargetAtTime(on ? params.mid : 0, t, T);
    nodes.eqHigh.gain.setTargetAtTime(on ? params.high : 0, t, T);
  },
  compressor: ({ nodes, t, on, params }) => {
    const T = 0.02;
    nodes.compressor.threshold.setTargetAtTime(on ? params.threshold : 0, t, T);
    nodes.compressor.ratio.setTargetAtTime(on ? params.ratio : 1, t, T);
  },
  limiter: ({ nodes, t, on, params }) => {
    const T = 0.02;
    nodes.limiter.threshold.setTargetAtTime(on ? params.threshold : 0, t, T);
    nodes.limiter.ratio.setTargetAtTime(on ? params.ratio : 1, t, T);
  },
  reverb: ({ nodes, t, on, params }) => {
    nodes.reverbSend.gain.setTargetAtTime(on ? params.send : 0, t, 0.05);
  },
  delay: ({ nodes, t, on, params }) => {
    // delayTime always reflects the user's setting even when bypassed so the
    // wet repeats stay in tempo. Graph is parallel — 0 feedback ⇒ silent.
    nodes.delay.delayTime.setTargetAtTime(Math.max(0.001, params.time), t, 0.05);
    nodes.delayFeedback.gain.setTargetAtTime(on ? params.feedback : 0, t, 0.05);
    nodes.delaySend.gain.setTargetAtTime(on ? 0.4 : 0, t, 0.05);
  },
  autotune: ({ nodes, t, on, params }) => {
    nodes.pitchLFO.frequency.setTargetAtTime(Math.max(0.05, params.rate), t, 0.05);
    nodes.pitchLFOGain.gain.setTargetAtTime(on ? params.depth : 0, t, 0.05);
  },
};

function applyEffectParams(nodes, name, enabled, ctx, params) {
  const recipe = EFFECT_RECIPES[name];
  if (recipe) recipe({ nodes, t: ctx.currentTime, on: enabled, params });
}

/** Build the per-channel FX chain in ANY AudioContext (used by both live
 *  engine and offline render).
 *
 *  Signal flow:
 *    input (eqLow) → eqMid → eqHigh → compressor → limiter → preTail
 *      preTail → direct → mixer
 *      preTail → reverbSend → convolver → reverbWet → mixer
 *      preTail → delaySend → delay (feedback loop) → delayWet → mixer
 *    mixer → output  (caller-owned next node — typically sidechainGain)
 *
 *  Returns { input, output, terminal, nodes, applyEffect, connectPitchTo,
 *  disconnectPitchFrom }. The plugin host can splice AudioWorklet nodes
 *  between `mixer` and `output` by reconnecting around `terminal`. */
export function buildFxChain(ctx, output, sharedReverbIR) {
  const eqLow = ctx.createBiquadFilter();
  eqLow.type = "lowshelf";
  eqLow.frequency.value = 200;
  eqLow.gain.value = 0;

  const eqMid = ctx.createBiquadFilter();
  eqMid.type = "peaking";
  eqMid.frequency.value = 1200;
  eqMid.Q.value = 0.9;
  eqMid.gain.value = 0;

  const eqHigh = ctx.createBiquadFilter();
  eqHigh.type = "highshelf";
  eqHigh.frequency.value = 6000;
  eqHigh.gain.value = 0;

  const compressor = ctx.createDynamicsCompressor();
  compressor.threshold.value = 0; compressor.knee.value = 30; compressor.ratio.value = 1;
  compressor.attack.value = 0.01; compressor.release.value = 0.15;

  const limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = 0; limiter.knee.value = 0; limiter.ratio.value = 1;
  limiter.attack.value = 0.001; limiter.release.value = 0.05;

  const preTail = ctx.createGain(); preTail.gain.value = 1;
  const mixer = ctx.createGain(); mixer.gain.value = 1;
  const direct = ctx.createGain(); direct.gain.value = 1;

  const reverbSend = ctx.createGain(); reverbSend.gain.value = 0;
  const convolver = ctx.createConvolver();
  convolver.buffer = sharedReverbIR || makeReverbIR(ctx);
  // iter-79: start the wet returns at 0 to match the engine's default
  // Dry-mode initialisation. Flipping Master Mode to Wet lifts these to 1.
  const reverbWet = ctx.createGain(); reverbWet.gain.value = 0;

  const delaySend = ctx.createGain(); delaySend.gain.value = 0;
  const delay = ctx.createDelay(2.0); delay.delayTime.value = 0.38;
  const delayFeedback = ctx.createGain(); delayFeedback.gain.value = 0.4;
  const delayWet = ctx.createGain(); delayWet.gain.value = 0;

  const pitchLFO = ctx.createOscillator();
  pitchLFO.frequency.value = 4.5;
  const pitchLFOGain = ctx.createGain();
  pitchLFOGain.gain.value = 0;
  pitchLFO.connect(pitchLFOGain);
  pitchLFO.start();

  // series
  eqLow.connect(eqMid); eqMid.connect(eqHigh); eqHigh.connect(compressor);
  compressor.connect(limiter); limiter.connect(preTail);

  // parallel taps
  preTail.connect(direct); direct.connect(mixer);
  preTail.connect(reverbSend); reverbSend.connect(convolver); convolver.connect(reverbWet); reverbWet.connect(mixer);
  preTail.connect(delaySend); delaySend.connect(delay); delay.connect(delayFeedback);
  delayFeedback.connect(delay); delay.connect(delayWet); delayWet.connect(mixer);

  // FxChain terminal node is `mixer`. The caller is responsible for wiring
  // `output` (`mixer`) into the next node in the signal chain — this leaves
  // room for the per-channel plugin host (engine._rewirePluginChain) to
  // insert AudioWorkletNodes between the FxChain and sidechainGain.
  mixer.connect(output);

  const nodes = { eqLow, eqMid, eqHigh, compressor, limiter, preTail, mixer, direct, reverbSend, convolver, reverbWet, delaySend, delay, delayFeedback, delayWet, pitchLFO, pitchLFOGain };

  return {
    input: eqLow,
    output: mixer,
    terminal: output,   // the node the FxChain originally piped into (sidechainGain)
    nodes,
    applyEffect(name, enabled, params) { applyEffectParams(nodes, name, enabled, ctx, params); },
    connectPitchTo(srcDetuneParam) {
      try { pitchLFOGain.connect(srcDetuneParam); } catch (e) { void e; /* detune may already be wired — non-fatal */ }
    },
    disconnectPitchFrom(srcDetuneParam) {
      try { pitchLFOGain.disconnect(srcDetuneParam); } catch (e) { void e; /* never connected — non-fatal */ }
    },
  };
}
